// frontend/src/widgets/JobListWidget.jsx
// Project:      Vision ANPR - Job List Widget (Jobs + Assignments merge)
// Author:       Leon du Plooy - Castle Traffic Ltd
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-07-08
// Updated:      2026-11-03 14:45 GMT
//
// Change Log (BST/GMT):
// 2026-11-03 14:45 GMT  A24: Nine selectable Job selling-value columns, currency/date formatting,
//                       search/sort integration; current value and currency visible by default.
//                       Preserves v26 per-user column settings and mobile card resizing.
// 12:46  Scheduled fields appear in list via latest assignment lookup.
// 13:55  Column defaults: include Scheduled Start/End; remove Model/Progress/Current Step.
// 14:05  Availability uses ISO without milliseconds to avoid 422.
// 14:11  Assignment save uses POST jobs/{id}/resources (nested route); no flat job-resource-assignments.
// 14:22  REMOVED job meta PATCH/PUT (jobs/{id}) entirely to stop 405 noise.  ← *still true for general meta*
// 15:36  ✨ Polishing: non-blocking toasts for success/error + “no changes”; keep auto-refresh.
// 16:22  🧹 Fix build: removed stray "thead;" and any unused bare expressions.
// 09:15  📍 Job location edit flow added (in modal originally).
// 12:10  🛠  ESLint fix: implement saveJobRef; remove duplicated stray block after saveDocFlags.
// 14:20  🧩 Extraction: Inline JobEditModal removed; now imported from components/JobEditModal.jsx
// 09:20  ➕ Column: Added "Co-Resource" to column chooser; reads latest assignment co_resource_id.
// 09:41  🛠  Co-Resource value resolves with multiple key fallbacks + job-level fallback.
// 10:07  🛠  Co-Resource inference improved: scan all rows by role/flags; fallback to most-recent
//             non-primary resourceId; handle arrays (co_resources) and nested objects.
// 14:31  ➕ Added selectable planning columns for Week Commencing + Planning Mode.
// 2026-09-01  ➕ Added selectable commercial lineage columns for Quote ID + Opportunity ID.
// 2026-09-01  🔐 Job-list column preferences now use authenticated tenant/user context; legacy unknownTenant v25 settings migrate once.
//
// ------------------------------------------------------------------
//
// Mobile Responsiveness (2026-09-08):
// - Mirrors the proven AdvancedContactListWidget mobile table/card pattern.
// - The SAME table DOM reflows below 768px: table->block, thead hidden,
//   tbody->block, each job row->labelled card, actions->full-width row.
// - No duplicate mobile job list and no duplicate API work.
// - Mobile Sort By + direction controls replace hidden table-header sorting.
// - Column Chooser adds touch-safe up/down ordering while desktop drag/drop remains.
// - Desktop virtualisation remains 41px-row based. Mobile virtualisation uses
//   the measured first-card height so spacer/prefetch maths matches real cards.
// - The mobile records viewport is sized to one COMPLETE job card per scroll
//   and uses mandatory snap, matching the accepted list-widget behaviour.
// - ReactGridLayout/mobile fixed hosts are expanded only for mobile presentation.
// - 14:50 BST runtime fix: declare visibleKeys before the mobile sizing hook
//   dependency array to prevent an initial-render temporal-dead-zone failure.
// ------------------------------------------------------------------

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axiosInstance from "../api/axiosInstance";
import { useDashboardRefresh } from "../context/DashboardRefreshContext";
import { useAuth } from "../context/AuthContext";
import { getWidgetTitle } from "../utils/widgetUtils";
import ModalWrapper from "../components/ModalWrapper";
import JobEditModal from "../components/JobEditModal";

// ---------- Tiny Toast ----------
const Toast = ({ message, type = "success", onClose }) => {
  if (!message) return null;
  const bg =
    type === "success"
      ? "bg-emerald-600"
      : type === "error"
      ? "bg-red-600"
      : "bg-slate-700";
  return (
    <div className="fixed bottom-5 right-5 z-[9999]">
      <div
        className={`${bg} text-white shadow-lg rounded-lg px-4 py-3 text-sm flex items-start gap-3`}
      >
        <span>{message}</span>
        <button
          onClick={onClose}
          className="ml-2 text-white/80 hover:text-white focus:outline-none"
          aria-label="Close toast"
          title="Close"
        >
          ×
        </button>
      </div>
    </div>
  );
};

// ---------- Helpers ----------
// ---------- General helpers ----------
const combineIdName = (id, name, fallbackLabel) => {
  if (id && name) return `${id} · ${name}`;
  if (id) return `${id} · ${fallbackLabel || ""}`.trim();
  return name || "";
};

const formatDateGB = (value) => {
  if (!value) return "";
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

const SELLING_MONEY_KEYS = new Set([
  "selling_value_ex_tax",
  "original_selling_value_ex_tax",
  "selling_tax_amount",
  "original_selling_tax_amount",
]);

const formatSellingAmount = (value, currency) => {
  if (value === null || value === undefined || value === "") return "";
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);
  // Never invent a currency for historical records with unknown currency.
  const code = typeof currency === "string" ? currency.trim().toUpperCase() : "";
  if (/^[A-Z]{3}$/.test(code)) {
    try {
      return new Intl.NumberFormat("en-GB", {
        style: "currency", currency: code,
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      }).format(number);
    } catch { /* Fall back to number-only formatting. */ }
  }
  return new Intl.NumberFormat("en-GB", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(number);
};

const formatDateOnlyGB = (value) => {
  if (!value) return "";
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

// ---------- Co-resource resolvers ----------
const resolveCoResourceIdFromRow = (row) => {
  if (!row || typeof row !== "object") return null;
  const direct =
    row.co_resource_id ??
    (row.co_resource && (row.co_resource.id ?? row.co_resource.resource_id)) ??
    row.coResourceId ??
    (row.coResource && (row.coResource.id ?? row.coResource.resource_id)) ??
    row.secondary_resource_id ??
    row.secondaryResourceId ??
    row.support_resource_id ??
    row.assistant_resource_id ??
    row.co_assigned_resource_id ??
    null;

  if (direct) return direct;

  if (Array.isArray(row.co_resources) && row.co_resources.length) {
    const first = row.co_resources.find((x) => x && (x.id || x.resource_id));
    if (first) return first.id ?? first.resource_id ?? null;
  }

  return null;
};

const roleLooksCo = (role) => {
  const val = String(role || "").toLowerCase();
  return ["co", "assistant", "support", "secondary", "co-resource", "coresource"].includes(
    val
  );
};

const rowFlagLooksCo = (row) =>
  !!(row?.is_co || row?.is_assistant || row?.is_support || row?.is_secondary);

const SCHEMA_VERSION = 26;

const normaliseStoragePart = (value, fallback) => {
  const raw = String(value ?? "").trim();
  return raw || fallback;
};

const storageKey = (userId, tenantId) =>
  `anpr_job_columns_v${SCHEMA_VERSION}_${tenantId}_${userId}`;

const legacyV25StorageKeys = (user) => {
  const username = normaliseStoragePart(
    user?.username ?? user?.user_name ?? user?.login ?? user?.email,
    ""
  );
  const id = normaliseStoragePart(user?.id ?? user?.user_id, "");
  const keys = new Set();

  if (username) keys.add(`anpr_job_columns_v25_unknownTenant_${username}`);
  if (id) keys.add(`anpr_job_columns_v25_unknownTenant_${id}`);

  return [...keys];
};

const loadStoredColumnSettings = (persistKey, legacyKeys = []) => {
  const read = (key) => {
    if (!key) return null;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };

  const current = read(persistKey);
  if (current) return current;

  for (const legacyKey of legacyKeys) {
    const legacy = read(legacyKey);
    if (!legacy) continue;

    try {
      localStorage.setItem(
        persistKey,
        JSON.stringify({ ...legacy, v: SCHEMA_VERSION })
      );
    } catch {}

    return { ...legacy, v: SCHEMA_VERSION };
  }

  return null;
};

const ALL_COLUMNS = [
  { key: "id", label: "ID" },
  { key: "job_ref", label: "Job Ref" },
  { key: "planning_mode", label: "Planning Mode" },
  { key: "week_commencing", label: "Week Commencing" },
  { key: "model_type", label: "Model Type" },
  { key: "status", label: "Status" },
  { key: "progress", label: "Progress (%)" },
  { key: "current_step", label: "Current Step" },
  { key: "task_id", label: "Task ID" },
  { key: "created_at", label: "Created At" },
  { key: "processed_at", label: "Processed At" },

  { key: "scheduled_start", label: "Scheduled Start" },
  { key: "scheduled_end", label: "Scheduled End" },

  { key: "notes", label: "Notes" },

  // Raw FK ids
  { key: "tenant_id", label: "Tenant (ID)" },
  { key: "customer_id", label: "Customer (ID)" },
  { key: "contact_id", label: "Contact (ID)" },
  { key: "quote_id", label: "Quote ID" },
  { key: "opportunity_id", label: "Opportunity ID" },

  // A24 commercial snapshot (financial values are read-only in this widget).
  { key: "selling_value_ex_tax", label: "Selling Value (ex VAT)" },
  { key: "original_selling_value_ex_tax", label: "Original Selling Value (ex VAT)" },
  { key: "selling_tax_amount", label: "Selling VAT" },
  { key: "original_selling_tax_amount", label: "Original Selling VAT" },
  { key: "selling_currency", label: "Selling Currency" },
  { key: "selling_value_source", label: "Selling Value Source" },
  { key: "selling_contract_reference", label: "Selling Contract Ref" },
  { key: "selling_value_recorded_at", label: "Selling Value Recorded" },
  { key: "selling_value_updated_at", label: "Selling Value Updated" },
  { key: "processed_by", label: "Processed By (ID)" },
  { key: "job_type_id", label: "Job Type (ID)" },
  { key: "job_subtype_id", label: "Job Subtype (ID)" },
  { key: "job_subtype2_id", label: "Job Subtype 2 (ID)" },

  // Friendly names (table-only)
  { key: "tenant_name", label: "Tenant" },
  { key: "customer_name", label: "Customer" },
  { key: "contact_name", label: "Contact" },
  { key: "processed_by_name", label: "Processed By" },
  { key: "job_type_name", label: "Job Type" },
  { key: "job_subtype_name", label: "Job Subtype" },
  { key: "job_subtype2_name", label: "Job Subtype 2" },

  // Friendly (derived)
  { key: "assigned_resource", label: "Assigned Resource" },
  { key: "co_resource", label: "Co-Resource" },
  { key: "location_name", label: "Location" },
];

const DEFAULT_VISIBLE_ORDER = [
  "job_ref",
  "customer_name",
  "location_name",
  "planning_mode",
  "week_commencing",
  "status",
  "selling_value_ex_tax",
  "selling_currency",
  "assigned_resource",
  "scheduled_start",
  "scheduled_end",
  "created_at",
];

const SEARCH_FIELDS = [
  "id",
  "job_ref",
  "planning_mode",
  "week_commencing",
  "model_type",
  "status",
  "current_step",
  "task_id",
  "notes",
  "tenant_id",
  "customer_id",
  "contact_id",
  "quote_id",
  "opportunity_id",
  "selling_value_ex_tax",
  "original_selling_value_ex_tax",
  "selling_tax_amount",
  "original_selling_tax_amount",
  "selling_currency",
  "selling_value_source",
  "selling_contract_reference",
  "selling_value_recorded_at",
  "selling_value_updated_at",
  "processed_by",
  "job_type_id",
  "job_subtype_id",
  "job_subtype2_id",
  "tenant_name",
  "customer_name",
  "contact_name",
  "processed_by_name",
  "job_type_name",
  "job_subtype_name",
  "job_subtype2_name",
  "assigned_resource",
  "co_resource",
  "location_name",
  "created_at",
  "processed_at",
  "scheduled_start",
  "scheduled_end",
];

// ---------- Column Chooser Modal ----------
const ColumnChooserModal = ({
  open,
  onClose,
  allColumns,
  visibilityMap,
  setVisibilityMap,
  order,
  setOrder,
  resetToDefault,
}) => {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const handleToggle = (key) => {
    setVisibilityMap((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const moveColumn = (key, direction) => {
    setOrder((prev) => {
      const fromIndex = prev.indexOf(key);
      const toIndex = fromIndex + direction;

      if (fromIndex < 0 || toIndex < 0 || toIndex >= prev.length) {
        return prev;
      }

      const next = prev.slice();
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const content = (
    <ModalWrapper onClose={onClose}>
      <h3 className="text-lg font-semibold mb-3">Choose Columns</h3>
      <p className="text-sm text-gray-600 mb-3">
        Tick the columns you want to see. Drag desktop table headers to reorder
        them, or use the mobile up/down controls below.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-[60vh] md:max-h-80 overflow-auto pr-1 md:pr-2">
        {order.map((key, index) => {
          const col = allColumns.find((c) => c.key === key);
          if (!col) return null;

          return (
            <div
              key={key}
              className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 p-2 md:border-0 md:rounded-none md:p-0"
            >
              <label className="flex min-w-0 flex-1 items-center gap-3 text-sm">
                <input
                  type="checkbox"
                  checked={!!visibilityMap[key]}
                  onChange={() => handleToggle(key)}
                  className="h-5 w-5 shrink-0 md:h-auto md:w-auto"
                />
                <span className="truncate">{col.label}</span>
              </label>

              <div className="flex shrink-0 gap-1 md:hidden">
                <button
                  type="button"
                  onClick={() => moveColumn(key, -1)}
                  disabled={index === 0}
                  className="min-h-[40px] min-w-[40px] rounded border border-gray-300 bg-white px-2 text-base font-semibold text-gray-700 disabled:opacity-35"
                  aria-label={`Move ${col.label} up`}
                  title={`Move ${col.label} up`}
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => moveColumn(key, 1)}
                  disabled={index === order.length - 1}
                  className="min-h-[40px] min-w-[40px] rounded border border-gray-300 bg-white px-2 text-base font-semibold text-gray-700 disabled:opacity-35"
                  aria-label={`Move ${col.label} down`}
                  title={`Move ${col.label} down`}
                >
                  ↓
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
        <button
          type="button"
          onClick={resetToDefault}
          className="min-h-[44px] w-full bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white text-sm font-semibold py-2 px-3 rounded shadow sm:w-auto"
        >
          Reset to Default
        </button>

        <button
          type="button"
          onClick={onClose}
          className="min-h-[44px] w-full bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick text-white text-sm font-semibold py-2 px-3 rounded shadow sm:w-auto"
        >
          Done
        </button>
      </div>
    </ModalWrapper>
  );

  const portalTarget = document.getElementById("modal-root") || document.body;
  return createPortal(content, portalTarget);
};

// ---------- Main Widget ----------
const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;
const ROW_HEIGHT = 41;
const ROW_OVERSCAN = 12;
const MOBILE_ROW_OVERSCAN = 3;
const MOBILE_CARD_GAP = 12;
const MOBILE_TBODY_VERTICAL_PADDING = 24;
const PREFETCH_VIEWPORTS = 3;

const JobListWidget = ({ onSelectJob }) => {
  const { refreshKey } = useDashboardRefresh();
  const { user } = useAuth();

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortField, setSortField] = useState("created_at");
  const [ascending, setAscending] = useState(false);

  // Job Edit still requires catalogue maps for editable select controls.
  // These are deliberately lazy-loaded only when the modal is opened, so the
  // Job List itself has no catalogue startup fan-out.
  const [customerMap, setCustomerMap] = useState({});
  const [contactMap, setContactMap] = useState({});
  const [userMap, setUserMap] = useState({});
  const [jobTypeMap, setJobTypeMap] = useState({});
  const [jobSubtypeMap, setJobSubtypeMap] = useState({});
  const [jobSubtype2Map, setJobSubtype2Map] = useState({});
  const [resourceMap, setResourceMap] = useState({});
  const [editMapsLoaded, setEditMapsLoaded] = useState(false);
  const [editMapsLoading, setEditMapsLoading] = useState(false);

  const [toast, setToast] = useState({ message: "", type: "success" });
  const toastTimerRef = useRef(null);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => {
      setToast({ message: "", type: "success" });
    }, 3500);
  };

  useEffect(
    () => () => {
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    },
    []
  );

  // Column UI state — preserve v26 authenticated tenant/user preference scope.
  const authUserId = useMemo(
    () =>
      normaliseStoragePart(
        user?.id ?? user?.user_id ?? user?.username ?? user?.email,
        "unknownUser"
      ),
    [user?.id, user?.user_id, user?.username, user?.email]
  );

  const authTenantId = useMemo(
    () => normaliseStoragePart(user?.tenant_id ?? user?.tenantId, "unknownTenant"),
    [user?.tenant_id, user?.tenantId]
  );

  const persistKey = useMemo(
    () => storageKey(authUserId, authTenantId),
    [authUserId, authTenantId]
  );

  const legacyKeys = useMemo(() => legacyV25StorageKeys(user), [
    user?.id,
    user?.user_id,
    user?.username,
    user?.user_name,
    user?.login,
    user?.email,
  ]);

  const [visibilityMap, setVisibilityMap] = useState(() => {
    const parsed = loadStoredColumnSettings(persistKey, legacyKeys);
    const map = { ...(parsed?.visibilityMap || {}) };
    ALL_COLUMNS.forEach((c) => {
      if (typeof map[c.key] === "undefined") {
        map[c.key] = DEFAULT_VISIBLE_ORDER.includes(c.key);
      }
    });
    map.scheduled_start = map.scheduled_start ?? true;
    map.scheduled_end = map.scheduled_end ?? true;
    map.planning_mode = map.planning_mode ?? true;
    map.week_commencing = map.week_commencing ?? true;
    map.model_type = map.model_type ?? false;
    map.progress = map.progress ?? false;
    map.current_step = map.current_step ?? false;
    map.co_resource = map.co_resource ?? false;
    map.quote_id = map.quote_id ?? false;
    map.opportunity_id = map.opportunity_id ?? false;
    return map;
  });

  const [order, setOrder] = useState(() => {
    const parsed = loadStoredColumnSettings(persistKey, legacyKeys);
    if (Array.isArray(parsed?.order) && parsed.order.length) {
      const saved = parsed.order;
      const missing = ALL_COLUMNS.map((c) => c.key).filter((k) => !saved.includes(k));
      return [...saved, ...missing];
    }
    const defaults = DEFAULT_VISIBLE_ORDER.slice();
    const rest = ALL_COLUMNS.map((c) => c.key).filter((k) => !defaults.includes(k));
    return [...defaults, ...rest];
  });

  // Derived visible column order must be declared before any hook dependency
  // arrays that reference it. Keeping this here avoids a temporal-dead-zone
  // runtime failure during the component's initial render.
  const visibleKeys = useMemo(
    () => order.filter((k) => visibilityMap[k]),
    [order, visibilityMap]
  );

  const [chooserOpen, setChooserOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [activeJobId, setActiveJobId] = useState(null);

  useEffect(() => {
    if (authTenantId === "unknownTenant" || authUserId === "unknownUser") return;

    const parsed = loadStoredColumnSettings(persistKey, legacyKeys);
    const savedOrder = Array.isArray(parsed?.order) ? parsed.order : [];
    const savedVisibility = parsed?.visibilityMap || {};

    const nextOrder = savedOrder.length
      ? [
          ...savedOrder,
          ...ALL_COLUMNS.map((c) => c.key).filter((k) => !savedOrder.includes(k)),
        ]
      : [
          ...DEFAULT_VISIBLE_ORDER,
          ...ALL_COLUMNS.map((c) => c.key).filter(
            (k) => !DEFAULT_VISIBLE_ORDER.includes(k)
          ),
        ];

    const nextVisibility = {};
    ALL_COLUMNS.forEach((c) => {
      nextVisibility[c.key] =
        typeof savedVisibility[c.key] === "boolean"
          ? savedVisibility[c.key]
          : DEFAULT_VISIBLE_ORDER.includes(c.key);
    });

    nextVisibility.scheduled_start =
      typeof savedVisibility.scheduled_start === "boolean"
        ? savedVisibility.scheduled_start
        : true;
    nextVisibility.scheduled_end =
      typeof savedVisibility.scheduled_end === "boolean"
        ? savedVisibility.scheduled_end
        : true;
    nextVisibility.planning_mode =
      typeof savedVisibility.planning_mode === "boolean"
        ? savedVisibility.planning_mode
        : true;
    nextVisibility.week_commencing =
      typeof savedVisibility.week_commencing === "boolean"
        ? savedVisibility.week_commencing
        : true;
    nextVisibility.model_type =
      typeof savedVisibility.model_type === "boolean"
        ? savedVisibility.model_type
        : false;
    nextVisibility.progress =
      typeof savedVisibility.progress === "boolean"
        ? savedVisibility.progress
        : false;
    nextVisibility.current_step =
      typeof savedVisibility.current_step === "boolean"
        ? savedVisibility.current_step
        : false;
    nextVisibility.co_resource =
      typeof savedVisibility.co_resource === "boolean"
        ? savedVisibility.co_resource
        : false;
    nextVisibility.quote_id =
      typeof savedVisibility.quote_id === "boolean"
        ? savedVisibility.quote_id
        : false;
    nextVisibility.opportunity_id =
      typeof savedVisibility.opportunity_id === "boolean"
        ? savedVisibility.opportunity_id
        : false;

    setOrder(nextOrder);
    setVisibilityMap(nextVisibility);
  }, [authTenantId, authUserId, persistKey, legacyKeys]);

  useEffect(() => {
    if (authTenantId === "unknownTenant" || authUserId === "unknownUser") return;
    localStorage.setItem(
      persistKey,
      JSON.stringify({ order, visibilityMap, v: SCHEMA_VERSION })
    );
  }, [order, visibilityMap, persistKey, authTenantId, authUserId]);

  // Debounced server-side search.
  useEffect(() => {
    const timer = window.setTimeout(
      () => setDebouncedSearch(searchQuery.trim()),
      SEARCH_DEBOUNCE_MS
    );
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  const requestGenerationRef = useRef(0);
  const abortRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const nextCursorRef = useRef(null);
  const hasMoreRef = useRef(false);
  const widgetRootRef = useRef(null);
  const mobileHeaderRef = useRef(null);
  const scrollRef = useRef(null);
  const firstCardRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(400);
  const [mobileLayoutActive, setMobileLayoutActive] = useState(false);
  const [mobileCardHeight, setMobileCardHeight] = useState(0);

  const projectJob = (j) => {
    const resourceId = j.resource_id ?? null;
    const coResourceId = j.co_resource_id ?? null;

    return {
      ...j,
      planning_mode: j.week_commencing ? "Week Commencing" : "Exact Schedule",
      week_commencing: j.week_commencing || "",
      scheduled_start: j.assignment_starts_at || "",
      scheduled_end: j.assignment_ends_at || "",
      assigned_resource_id: resourceId,
      assigned_resource_name: j.resource_name || "",
      assigned_resource: combineIdName(resourceId, j.resource_name, "Resource"),
      co_resource_id: coResourceId,
      co_resource_name: j.co_resource_name || "",
      co_resource: combineIdName(coResourceId, j.co_resource_name, "Co-Resource"),
      location_name:
        j.location_name || (j.location_id ? `Location #${j.location_id}` : ""),
      tenant_name: j.tenant_name || (j.tenant_id ? `Tenant #${j.tenant_id}` : ""),
      customer_name:
        j.customer_name || (j.customer_id ? `Customer #${j.customer_id}` : ""),
      contact_name:
        j.contact_name || (j.contact_id ? `Contact #${j.contact_id}` : ""),
      processed_by_name:
        j.processed_by_name || (j.processed_by ? `User #${j.processed_by}` : ""),
      job_type_name:
        j.job_type_name || (j.job_type_id ? `Type #${j.job_type_id}` : ""),
      job_subtype_name:
        j.job_subtype_name || (j.job_subtype_id ? `Subtype #${j.job_subtype_id}` : ""),
      job_subtype2_name:
        j.job_subtype2_name ||
        (j.job_subtype2_id ? `Subtype2 #${j.job_subtype2_id}` : ""),
    };
  };

  const fetchPage = async ({ cursor = null, append = false, generation }) => {
    if (append && loadingMoreRef.current) return null;

    if (append) {
      loadingMoreRef.current = true;
      setLoadingMore(true);
    } else {
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();
      setLoading(true);
      setLoadError("");
    }

    try {
      const params = {
        limit: PAGE_SIZE,
        sort: sortField,
        direction: ascending ? "asc" : "desc",
      };
      if (debouncedSearch) params.q = debouncedSearch;
      if (cursor?.value !== undefined && cursor?.value !== null) {
        params.cursor_value = String(cursor.value);
        params.cursor_id = cursor.id;
      }

      const res = await axiosInstance.get("jobs/query", {
        params,
        signal: append ? undefined : abortRef.current.signal,
      });

      if (generation !== requestGenerationRef.current) return null;

      const data = res?.data || {};
      const incoming = Array.isArray(data.items) ? data.items.map(projectJob) : [];

      setJobs((previous) => {
        if (!append) return incoming;
        const seen = new Set(previous.map((j) => j.id));
        return [...previous, ...incoming.filter((j) => !seen.has(j.id))];
      });

      const cursorOut = data.next_cursor || null;
      const more = !!data.has_more && !!cursorOut;
      nextCursorRef.current = cursorOut;
      hasMoreRef.current = more;
      setNextCursor(cursorOut);
      setHasMore(more);
      return data;
    } catch (err) {
      if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return null;
      console.error("Failed to fetch Job List stream:", err?.response?.data || err?.message || err);
      if (generation === requestGenerationRef.current) {
        setLoadError("Unable to load job records.");
        if (!append) {
          setJobs([]);
          setHasMore(false);
          setNextCursor(null);
          hasMoreRef.current = false;
          nextCursorRef.current = null;
        }
      }
      return null;
    } finally {
      if (append) {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      } else if (generation === requestGenerationRef.current) {
        setLoading(false);
      }
    }
  };

  // Reset the stream whenever search/sort/dashboard refresh changes.
  useEffect(() => {
    const generation = requestGenerationRef.current + 1;
    requestGenerationRef.current = generation;
    nextCursorRef.current = null;
    hasMoreRef.current = false;
    setNextCursor(null);
    setHasMore(false);
    setJobs([]);
    setScrollTop(0);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;

    fetchPage({ append: false, generation });

    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
    // fetchPage intentionally captures the current stream parameters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey, debouncedSearch, sortField, ascending]);

  const loadNextPage = async () => {
    if (!hasMoreRef.current || !nextCursorRef.current || loadingMoreRef.current) return;
    const generation = requestGenerationRef.current;
    await fetchPage({
      cursor: nextCursorRef.current,
      append: true,
      generation,
    });
  };

  const effectiveRowHeight = mobileLayoutActive && mobileCardHeight > 0
    ? mobileCardHeight + MOBILE_CARD_GAP
    : ROW_HEIGHT;

  // Keep several viewports of already-fetched rows ahead of the user's current
  // position. Desktop stays 41px-row based; mobile uses the measured card step.
  useEffect(() => {
    if (loading || loadingMore || !hasMore) return;

    const loadedHeight = jobs.length * effectiveRowHeight;
    const desiredAhead = viewportHeight * PREFETCH_VIEWPORTS;
    const remainingLoaded = loadedHeight - (scrollTop + viewportHeight);

    if (remainingLoaded < desiredAhead) {
      loadNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    jobs.length,
    scrollTop,
    viewportHeight,
    loading,
    loadingMore,
    hasMore,
    effectiveRowHeight,
  ]);

  // Track viewport height without adding a package dependency.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    const update = () => setViewportHeight(Math.max(1, el.clientHeight || 400));
    update();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }

    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Mobile sizing mirrors the proven AdvancedContactListWidget behaviour.
  // CSS controls table->card presentation. This effect only measures the actual
  // rendered card, updates virtualisation geometry and expands a fixed mobile host.
  useLayoutEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const root = widgetRootRef.current;
    const header = mobileHeaderRef.current;
    const viewport = scrollRef.current;
    if (!root || !header || !viewport) return undefined;

    const mediaQuery = window.matchMedia("(max-width: 767px)");
    let animationFrame = 0;
    let hostRecord = null;

    const restoreHost = () => {
      if (!hostRecord?.node) return;
      hostRecord.node.style.height = hostRecord.height;
      hostRecord.node.style.minHeight = hostRecord.minHeight;
      hostRecord = null;
    };

    const findFixedMobileHost = () => {
      const rootRect = root.getBoundingClientRect();
      const candidates = [];
      let node = root.parentElement;
      let depth = 0;

      while (node && node !== document.body && depth < 6) {
        const rect = node.getBoundingClientRect();
        const inlineHeight = String(node.style?.height || "").trim();
        const widthMatches = Math.abs(rect.width - rootRect.width) < 90;
        const hasFixedInlineHeight =
          inlineHeight &&
          inlineHeight !== "auto" &&
          inlineHeight !== "100%" &&
          inlineHeight !== "100vh";

        if (widthMatches && hasFixedInlineHeight) {
          candidates.push(node);
        }

        node = node.parentElement;
        depth += 1;
      }

      return candidates.length ? candidates[0] : null;
    };

    const applyMobileSizing = () => {
      cancelAnimationFrame(animationFrame);
      restoreHost();

      viewport.style.height = "";
      viewport.style.flex = "";
      root.style.height = "";
      root.style.minHeight = "";

      const active = mediaQuery.matches;
      setMobileLayoutActive(active);

      if (!active) {
        setMobileCardHeight(0);
        return;
      }

      animationFrame = requestAnimationFrame(() => {
        const firstCard = firstCardRef.current;
        const cardHeight = firstCard
          ? Math.ceil(firstCard.getBoundingClientRect().height)
          : 180;

        setMobileCardHeight(cardHeight);

        const recordViewportHeight = cardHeight + MOBILE_TBODY_VERTICAL_PADDING;
        viewport.style.height = `${recordViewportHeight}px`;
        viewport.style.flex = "0 0 auto";

        root.style.height = "auto";
        root.style.minHeight = "0px";

        animationFrame = requestAnimationFrame(() => {
          const host = findFixedMobileHost();
          if (!host) return;

          hostRecord = {
            node: host,
            height: host.style.height,
            minHeight: host.style.minHeight,
          };

          const hostRect = host.getBoundingClientRect();
          const rootRect = root.getBoundingClientRect();
          const wrapperChromeHeight = Math.max(0, rootRect.top - hostRect.top);
          const requiredHostHeight = Math.ceil(
            Math.max(host.scrollHeight, wrapperChromeHeight + rootRect.height + 8)
          );

          host.style.height = `${requiredHostHeight}px`;
          host.style.minHeight = `${requiredHostHeight}px`;
        });
      });
    };

    applyMobileSizing();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => applyMobileSizing())
        : null;

    if (resizeObserver) {
      resizeObserver.observe(header);
      if (firstCardRef.current) resizeObserver.observe(firstCardRef.current);
    }

    const handleMediaChange = () => applyMobileSizing();
    mediaQuery.addEventListener?.("change", handleMediaChange);

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();
      mediaQuery.removeEventListener?.("change", handleMediaChange);
      restoreHost();
      viewport.style.height = "";
      viewport.style.flex = "";
      root.style.height = "";
      root.style.minHeight = "";
    };
  }, [jobs, visibleKeys]);

  // Lazy-load Job Edit catalogues on demand, but complete the load BEFORE
  // opening the modal. JobEditModal expects these maps to exist when its form
  // is initialised. Keeping this event-driven avoids the former startup fan-out
  // without racing the modal against asynchronous map state.
  const editMapsPromiseRef = useRef(null);

  const ensureEditMaps = async () => {
    if (editMapsLoaded) return true;
    if (editMapsPromiseRef.current) return editMapsPromiseRef.current;

    const toMap = (arr, getLabel) => {
      const map = {};
      (Array.isArray(arr) ? arr : []).forEach((x) => {
        map[x.id] = getLabel(x);
      });
      return map;
    };

    const request = (async () => {
      setEditMapsLoading(true);

      try {
        const [
          customersRes,
          contactsRes,
          usersRes,
          jobTypesRes,
          jobSubtypesRes,
          jobSubtypes2Res,
          resourcesRes,
        ] = await Promise.allSettled([
          axiosInstance.get("customers?limit=1000"),
          axiosInstance.get("contacts?limit=1000"),
          axiosInstance.get("users?limit=1000"),
          axiosInstance.get("job-types?limit=1000"),
          axiosInstance.get("job-subtypes?limit=1000"),
          axiosInstance.get("job-subtypes2?limit=1000"),
          axiosInstance.get("resources?limit=2000"),
        ]);

        const failures = [
          ["customers", customersRes],
          ["contacts", contactsRes],
          ["users", usersRes],
          ["job-types", jobTypesRes],
          ["job-subtypes", jobSubtypesRes],
          ["job-subtypes2", jobSubtypes2Res],
          ["resources", resourcesRes],
        ].filter(([, result]) => result.status !== "fulfilled");

        if (customersRes.status === "fulfilled") {
          setCustomerMap(
            toMap(
              customersRes.value.data || [],
              (c) => c.name || c.nickname || `Customer #${c.id}`
            )
          );
        }

        if (contactsRes.status === "fulfilled") {
          setContactMap(
            toMap(contactsRes.value.data || [], (c) =>
              [c.first_name, c.last_name].filter(Boolean).join(" ").trim() ||
              c.email ||
              `Contact #${c.id}`
            )
          );
        }

        if (usersRes.status === "fulfilled") {
          setUserMap(
            toMap(usersRes.value.data || [], (u) =>
              [u.first_name, u.last_name].filter(Boolean).join(" ").trim() ||
              u.username ||
              u.email ||
              `User #${u.id}`
            )
          );
        }

        if (jobTypesRes.status === "fulfilled") {
          setJobTypeMap(
            toMap(
              jobTypesRes.value.data || [],
              (jt) => jt.name || `Type #${jt.id}`
            )
          );
        }

        if (jobSubtypesRes.status === "fulfilled") {
          setJobSubtypeMap(
            toMap(
              jobSubtypesRes.value.data || [],
              (js) => js.name || `Subtype #${js.id}`
            )
          );
        }

        if (jobSubtypes2Res.status === "fulfilled") {
          setJobSubtype2Map(
            toMap(
              jobSubtypes2Res.value.data || [],
              (js2) => js2.name || `Subtype2 #${js2.id}`
            )
          );
        }

        if (resourcesRes.status === "fulfilled") {
          setResourceMap(
            toMap(resourcesRes.value.data || [], (r) =>
              [r.first_name, r.last_name].filter(Boolean).join(" ").trim() ||
              r.email ||
              `Resource #${r.id}`
            )
          );
        }

        if (failures.length) {
          console.warn(
            "One or more Job Edit catalogues failed to load:",
            failures.map(([name, result]) => ({
              name,
              error:
                result.reason?.response?.data ||
                result.reason?.message ||
                String(result.reason || "Unknown error"),
            }))
          );
        }

        setEditMapsLoaded(true);
        return true;
      } catch (err) {
        console.error(
          "Failed to load Job Edit catalogues:",
          err?.response?.data || err?.message || err
        );
        showToast("Unable to load Job Edit options.", "error");
        return false;
      } finally {
        setEditMapsLoading(false);
        editMapsPromiseRef.current = null;
      }
    })();

    editMapsPromiseRef.current = request;
    return request;
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setAscending((a) => !a);
    } else {
      setSortField(field);
      setAscending(true);
    }
  };

  const dragIndexRef = useRef(null);

  const onHeaderDragStart = (e, key) => {
    dragIndexRef.current = visibleKeys.indexOf(key);
    e.dataTransfer.effectAllowed = "move";
  };

  const onHeaderDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const onHeaderDrop = (e, dropKey) => {
    e.preventDefault();
    const fromIdx = dragIndexRef.current;
    const toIdx = visibleKeys.indexOf(dropKey);
    if (fromIdx === null || fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return;

    const newVisible = visibleKeys.slice();
    const [moved] = newVisible.splice(fromIdx, 1);
    newVisible.splice(toIdx, 0, moved);

    const hidden = order.filter((k) => !visibilityMap[k]);
    setOrder([...newVisible, ...hidden]);
    dragIndexRef.current = null;
  };

  const renderSortIndicator = (field) =>
    field === sortField ? (ascending ? " ▲" : " ▼") : null;

  const handleOpen = async (jobId) => {
    if (editMapsLoading) return;

    const mapsReady = await ensureEditMaps();
    if (!mapsReady) return;

    setActiveJobId(jobId);
    setEditOpen(true);
    onSelectJob?.(jobId);
  };

  const resetToDefault = () => {
    const map = {};
    ALL_COLUMNS.forEach((c) => (map[c.key] = DEFAULT_VISIBLE_ORDER.includes(c.key)));
    map.scheduled_start = true;
    map.scheduled_end = true;
    map.planning_mode = true;
    map.week_commencing = true;
    map.model_type = false;
    map.progress = false;
    map.current_step = false;
    map.co_resource = false;
    map.quote_id = false;
    map.opportunity_id = false;

    setVisibilityMap(map);

    const defaults = DEFAULT_VISIBLE_ORDER.slice();
    const rest = ALL_COLUMNS.map((c) => c.key).filter((k) => !defaults.includes(k));
    setOrder([...defaults, ...rest]);

    setSortField("created_at");
    setAscending(false);
  };

  const handleSaved = (result) => {
    if (result?.ok) {
      showToast(result.message || "Saved.", "success");
    } else if (result?.message) {
      showToast(result.message, "error");
    }
    window.dispatchEvent(new CustomEvent("dashboard:refresh"));
  };

  const onScroll = (e) => {
    setScrollTop(e.currentTarget.scrollTop);
  };

  const totalRows = jobs.length;
  const activeOverscan = mobileLayoutActive ? MOBILE_ROW_OVERSCAN : ROW_OVERSCAN;
  const startIndex = Math.max(
    0,
    Math.floor(scrollTop / effectiveRowHeight) - activeOverscan
  );
  const visibleCount =
    Math.ceil(viewportHeight / effectiveRowHeight) + activeOverscan * 2;
  const endIndex = Math.min(totalRows, startIndex + visibleCount);
  const virtualJobs = jobs.slice(startIndex, endIndex);
  const topSpacerHeight = startIndex * effectiveRowHeight;
  const bottomSpacerHeight = Math.max(
    0,
    (totalRows - endIndex) * effectiveRowHeight
  );

  const cellValue = (job, key) => {
    if (
      key === "created_at" ||
      key === "processed_at" ||
      key === "scheduled_start" ||
      key === "scheduled_end" ||
      key === "selling_value_recorded_at" ||
      key === "selling_value_updated_at"
    ) {
      return formatDateGB(job[key]);
    }
    if (key === "week_commencing") return formatDateOnlyGB(job[key]);
    if (SELLING_MONEY_KEYS.has(key)) return formatSellingAmount(job[key], job.selling_currency);
    if (key === "progress") {
      const value = job[key];
      if (value === null || value === undefined || value === "") return "";
      const number = Number(value);
      return Number.isFinite(number) ? `${number}%` : String(value);
    }
    if (key === "tenant_id") {
      return combineIdName(job.tenant_id, job.tenant_name, "Tenant");
    }
    if (key === "customer_id") {
      return combineIdName(job.customer_id, job.customer_name, "Customer");
    }
    if (key === "contact_id") {
      return combineIdName(job.contact_id, job.contact_name, "Contact");
    }
    if (key === "processed_by") {
      return combineIdName(job.processed_by, job.processed_by_name, "User");
    }
    if (key === "job_type_id") {
      return combineIdName(job.job_type_id, job.job_type_name, "Type");
    }
    if (key === "job_subtype_id") {
      return combineIdName(job.job_subtype_id, job.job_subtype_name, "Subtype");
    }
    if (key === "job_subtype2_id") {
      return combineIdName(job.job_subtype2_id, job.job_subtype2_name, "Subtype2");
    }
    if (key === "assigned_resource") {
      return combineIdName(
        job.assigned_resource_id,
        job.assigned_resource_name,
        "Resource"
      );
    }
    if (key === "co_resource") {
      return combineIdName(job.co_resource_id, job.co_resource_name, "Co-Resource");
    }
    return job[key] ?? "";
  };

  return (
    <div
      ref={widgetRootRef}
      className="h-auto min-h-0 w-full overflow-hidden rounded-lg border border-gray-200 shadow-sm bg-white flex flex-col relative md:h-full"
    >
      <Toast
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ message: "", type: "success" })}
      />

      <div
        ref={mobileHeaderRef}
        className="bg-accent text-white px-3 py-3 md:px-4 md:py-2 flex flex-col gap-3 md:flex-row md:flex-wrap md:justify-between md:items-center"
      >
        <span className="text-sm font-semibold">
          Search the {getWidgetTitle("job_list")}
        </span>

        <div className="grid w-full grid-cols-[minmax(0,1fr)_auto] gap-2 md:flex md:w-auto md:items-center">
          <input
            type="text"
            placeholder="Search jobs..."
            className="min-h-[44px] min-w-0 w-full rounded border border-white bg-white px-3 py-2 text-sm text-black md:min-h-0 md:w-56 md:flex-none md:px-2 md:py-1"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button
            type="button"
            onClick={() => setChooserOpen(true)}
            className="min-h-[44px] whitespace-nowrap bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white text-xs font-semibold px-3 py-2 rounded shadow md:min-h-0 md:py-1"
            title="Choose Columns"
          >
            Column Chooser
          </button>
        </div>

        <div className="grid w-full grid-cols-[minmax(0,1fr)_auto] gap-2 md:hidden">
          <label className="flex min-w-0 flex-col gap-1 text-xs font-semibold">
            Sort by
            <select
              value={sortField}
              onChange={(event) => {
                setSortField(event.target.value);
                setAscending(true);
              }}
              className="min-h-[44px] w-full rounded border border-white bg-white px-3 py-2 text-sm font-normal text-black"
            >
              {ALL_COLUMNS.map((column) => (
                <option key={column.key} value={column.key}>
                  {column.label}
                </option>
              ))}
            </select>
          </label>

          <button
            type="button"
            onClick={() => setAscending((current) => !current)}
            className="min-h-[44px] self-end rounded bg-btnGeneralNormal px-4 py-2 text-sm font-semibold text-white shadow hover:bg-btnGeneralHover active:bg-btnGeneralClick"
            aria-label={ascending ? "Sort descending" : "Sort ascending"}
            title={
              ascending
                ? "Currently ascending — tap for descending"
                : "Currently descending — tap for ascending"
            }
          >
            {ascending ? "▲" : "▼"}
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="min-h-0 w-full overflow-y-auto overflow-x-hidden overscroll-contain scroll-smooth snap-y snap-mandatory md:flex-1 md:overflow-auto md:snap-none"
        onScroll={onScroll}
      >
        <table className="block w-full text-left text-sm text-gray-700 md:table">
          <thead className="hidden md:table-header-group">
            <tr className="bg-accent text-white select-none">
              {visibleKeys.map((key) => {
                const col = ALL_COLUMNS.find((c) => c.key === key);
                if (!col) return null;
                return (
                  <th
                    key={key}
                    className="px-4 py-2 cursor-move sticky top-0 z-10 bg-accent whitespace-nowrap"
                    draggable
                    onDragStart={(e) => onHeaderDragStart(e, key)}
                    onDragOver={onHeaderDragOver}
                    onDrop={(e) => onHeaderDrop(e, key)}
                    onClick={() => handleSort(key)}
                    title="Drag to reorder • Click to sort"
                  >
                    {col.label}
                    {renderSortIndicator(key)}
                  </th>
                );
              })}
              <th className="px-4 py-2 text-center sticky top-0 z-10 bg-accent">
                Actions
              </th>
            </tr>
          </thead>

          <tbody className="block space-y-3 p-3 md:table-row-group md:space-y-0 md:p-0">
            {loading && jobs.length === 0 ? (
              <tr className="block md:table-row">
                <td
                  colSpan={visibleKeys.length + 1}
                  className="block rounded-lg border border-gray-200 bg-white px-4 py-5 text-center text-gray-500 italic md:table-cell md:rounded-none md:border-0"
                >
                  Loading job records...
                </td>
              </tr>
            ) : loadError && jobs.length === 0 ? (
              <tr className="block md:table-row">
                <td
                  colSpan={visibleKeys.length + 1}
                  className="block rounded-lg border border-red-200 bg-white px-4 py-5 text-center text-red-600 md:table-cell md:rounded-none md:border-0"
                >
                  {loadError}
                </td>
              </tr>
            ) : jobs.length === 0 ? (
              <tr className="block md:table-row">
                <td
                  colSpan={visibleKeys.length + 1}
                  className="block rounded-lg border border-gray-200 bg-white px-4 py-5 text-center text-gray-500 italic md:table-cell md:rounded-none md:border-0"
                >
                  No jobs found.
                </td>
              </tr>
            ) : (
              <>
                {topSpacerHeight > 0 && (
                  <tr
                    aria-hidden="true"
                    className="block md:table-row"
                    style={{ height: `${topSpacerHeight}px` }}
                  >
                    <td
                      colSpan={visibleKeys.length + 1}
                      className="block md:table-cell"
                      style={{ height: `${topSpacerHeight}px`, padding: 0, border: 0 }}
                    />
                  </tr>
                )}

                {virtualJobs.map((job, index) => (
                  <tr
                    key={job.id}
                    ref={index === 0 ? firstCardRef : undefined}
                    className="block snap-start scroll-mt-3 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm cursor-pointer hover:bg-gray-50 md:table-row md:h-[41px] md:rounded-none md:border-x-0 md:border-t-0 md:border-b md:border-gray-100 md:shadow-none md:odd:bg-gray-50 md:even:bg-white md:hover:bg-gray-100"
                    onClick={() => handleOpen(job.id)}
                  >
                    {visibleKeys.map((key) => {
                      const col = ALL_COLUMNS.find((column) => column.key === key);
                      if (!col) return null;

                      return (
                        <td
                          key={key}
                          className="flex items-start justify-between gap-4 border-b border-gray-100 px-4 py-3 md:table-cell md:border-b-0 md:px-4 md:py-2 md:whitespace-nowrap"
                        >
                          <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-gray-500 md:hidden">
                            {col.label}
                          </span>
                          <span
                            className="min-w-0 break-words text-right md:text-left"
                            title={String(cellValue(job, key) ?? "")}
                          >
                            {cellValue(job, key) || "—"}
                          </span>
                        </td>
                      );
                    })}

                    <td className="block px-4 py-3 md:table-cell md:px-4 md:py-2 md:text-center md:whitespace-nowrap">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpen(job.id);
                        }}
                        className="min-h-[44px] w-full bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick text-white text-sm font-body px-3 py-2 rounded shadow transition-colors duration-200 ease-in-out md:min-h-0 md:w-auto md:text-xs md:py-1"
                        title="Open Job"
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                ))}

                {bottomSpacerHeight > 0 && (
                  <tr
                    aria-hidden="true"
                    className="block md:table-row"
                    style={{ height: `${bottomSpacerHeight}px` }}
                  >
                    <td
                      colSpan={visibleKeys.length + 1}
                      className="block md:table-cell"
                      style={{ height: `${bottomSpacerHeight}px`, padding: 0, border: 0 }}
                    />
                  </tr>
                )}

                {loadingMore && (
                  <tr className="block md:table-row">
                    <td
                      colSpan={visibleKeys.length + 1}
                      className="block rounded-lg border border-gray-200 bg-white px-4 py-3 text-center text-gray-500 italic md:table-cell md:rounded-none md:border-0 md:py-2"
                    >
                      Loading more jobs...
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>

      <ColumnChooserModal
        open={chooserOpen}
        onClose={() => setChooserOpen(false)}
        allColumns={ALL_COLUMNS}
        visibilityMap={visibilityMap}
        setVisibilityMap={setVisibilityMap}
        order={order}
        setOrder={setOrder}
        resetToDefault={resetToDefault}
      />

      <JobEditModal
        open={editOpen}
        jobId={activeJobId}
        onClose={() => setEditOpen(false)}
        onSaved={handleSaved}
        maps={{
          customerMap,
          contactMap,
          userMap,
          jobTypeMap,
          jobSubtypeMap,
          jobSubtype2Map,
          resourceMap,
        }}
      />
    </div>
  );
};

export default JobListWidget;
